import React from 'react'
import { Box, Card, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material'
import {
  CLASSE_SCORE_LABEL,
  ScoreComercial,
  classeScoreChipColor,
} from '@/types/scoreComercial'

/**
 * Score Comercial da reserva (PDF Pipeline §2.1, decisão 8.1 de 28/09): BANT, APGAR e
 * IMPACTO, total, classe e a versão da tabela.
 *
 * Tudo aqui VEM do backend (`scoreComercial` no detalhe), derivado da etapa do funil
 * pela tabela versionada `ScoreEtapa`. Este cartão não calcula, não soma e não conhece a
 * tabela — se o número não vier, diz que não veio. Convive com a "Aderência da reserva"
 * (cartão ao lado, rótulo próprio): aquela responde "o que falta para instruir", esta
 * responde "em que etapa está".
 */
interface Props {
  score?: ScoreComercial | null
}

const DIMENSOES: { chave: keyof Pick<ScoreComercial, 'bant' | 'apgar' | 'impacto'>; rotulo: string }[] = [
  { chave: 'bant', rotulo: 'BANT' },
  { chave: 'apgar', rotulo: 'APGAR' },
  { chave: 'impacto', rotulo: 'IMPACTO' },
]

/** Cor do total acompanha a do chip de classe (mesma semântica do tema). */
const corDoTotal = (score: ScoreComercial): string => {
  const cor = classeScoreChipColor(score.classe)
  return cor === 'default' ? 'text.primary' : `${cor}.main`
}

const ScoreComercialCard: React.FC<Props> = ({ score }) => (
  <Card sx={{ mb: 3 }} data-testid="score-comercial-card">
    <CardHeader
      title="Score Comercial"
      subheader="Derivado da etapa do funil pela tabela versionada — nunca digitado"
    />
    <CardContent>
      {!score ? (
        <Typography variant="body2" color="textSecondary">
          Score Comercial indisponível nesta resposta.
        </Typography>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700 }}
              color={corDoTotal(score)}
              data-testid="score-comercial-total"
            >
              {score.total}
            </Typography>
            <Box>
              <Chip
                label={`Classe ${CLASSE_SCORE_LABEL[score.classe]}`}
                color={classeScoreChipColor(score.classe)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                pontos na etapa atual
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1}>
            {DIMENSOES.map((d) => (
              <Stack key={d.chave} direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {d.rotulo}
                </Typography>
                <Typography variant="body2" data-testid={`score-comercial-${d.chave}`}>
                  {score[d.chave]}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
            Tabela {score.versao}. O score muda só quando a etapa muda; cada mudança fica no histórico.
          </Typography>
        </>
      )}
    </CardContent>
  </Card>
)

export default ScoreComercialCard
